import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { logout } from "../../redux/actions/authActions";

import useGrowthLoop from "./hooks/useGrowthLoop";
import GrowthLoopFnB from "./GrowthLoopFnB";
import GrowthLoopUserManual from "./GrowthLoopUserManual";
import GrowthLoopDisclaimer from "./GrowthLoopDisclaimer";
import GrowthLoopNEPNHM from "./GrowthLoopNEPNHM";
import GrowthLoopAbout from "./GrowthLoopAbout";
import GrowthLoopThinky from "./GrowthLoopThinky";
import GrowthLoopDashboard from "./GrowthLoopDashboard";
import GLAssessment from "./components/GLAssessment";
import GLOverallAssessment from "./components/GLOverallAssessment";
import GLChildProfile from "./components/GLChildProfile";
import GLHistory from "./components/GLHistory";
import GLAssessors from "./components/GLAssessors";
import GLPdfExporter from "./components/GLPdfExporter";
import GLCrossViewDashboard from "./components/GLCrossViewDashboard";
import GLOpenDashboard from "./components/GLOpenDashboard";

import {
  GenerateGLMCode,
  GenerateGLPCode,
  GenerateGLECode,
  GenerateGLDCode,
  GenerateGLXCode,
} from "../../components/GenerateThink5whysCode";

import { DOMAINS, DEEP_DIVE_DOMAINS, DEEP_DIVE_CTAS } from "./GrowthLoopSeed";

import {
  CHILD_ISSUES_PART1,
  CHILD_ISSUES_PART2,
  CHILD_ISSUES_PART3,
} from "./GrowthLoopSeedAll";

// ─── Constants ────────────────────────────────────────────────────────────────

const SCALE = [0, 1, 2, 3];
const ROLES = ["parent", "educator", "doctor", "observer"];

const POLICY_BENCHMARKS = {
  screen: 40,
  executive: 35,
  emotional: 30,
  esteem: 25,
  learning: 30,
  adhd: 45,
};

// ─── Tab definitions ──────────────────────────────────────────────────────────
// Groups: assess | understand | manage | learn
// activeTab matches t.id (number), not t.label

export const tabs = [
  // ── Assess ──
  {
    id: 4,
    group: "assess",
    label: { en: "Parent", hi: "अभिभावक" },
    icon: "fa-house-user",
  },
  {
    id: 5,
    group: "assess",
    label: { en: "Educator", hi: "शिक्षक" },
    icon: "fa-school",
  },
  {
    id: 6,
    group: "assess",
    label: { en: "Doctor", hi: "डॉक्टर" },
    icon: "fa-user-doctor",
  },
  {
    id: 31,
    group: "assess",
    label: { en: "Observer", hi: "अवलोकक" },
    icon: "fa-people-group",
  },
  {
    id: 29,
    group: "assess",
    label: { en: "Overall", hi: "समग्र" },
    icon: "fa-chart-pie",
  },
  // ── Understand ──
  {
    id: 30,
    group: "understand",
    label: { en: "Child", hi: "बच्चा" },
    icon: "fa-gauge-high",
  },
  {
    id: 13,
    group: "understand",
    label: { en: "Cross-View", hi: "तुलनात्मक" },
    icon: "fa-eye",
  },
  {
    id: 12,
    group: "understand",
    label: { en: "Benchmark", hi: "बेंचमार्क" },
    icon: "fa-chart-line",
  },
  // ── Manage ──
  {
    id: 3,
    group: "manage",
    label: { en: "Child Profile", hi: "बच्चा" },
    icon: "fa-child",
  },
  {
    id: 17,
    group: "manage",
    label: { en: "Codes", hi: "कोड" },
    icon: "fa-clipboard-check",
  },
  {
    id: 18,
    group: "manage",
    label: { en: "Assessors", hi: "अंकक" },
    icon: "fa-users-gear",
  },
  {
    id: 19,
    group: "manage",
    label: { en: "History", hi: "इतिहास" },
    icon: "fa-clock-rotate-left",
  },
  // ── CTA ──
  {
    id: 32,
    group: "cta",
    label: { en: "Quick Dashboard", hi: "त्वरित डैशबोर्ड" },
    icon: "fa-gauge-high",
  },
  {
    id: 33,
    group: "cta",
    label: { en: "Daily Action", hi: "दैनिक कार्य" },
    icon: "fa-calendar-day",
  },
  {
    id: 34,
    group: "cta",
    label: { en: "Weekly Improvement", hi: "साप्ताहिक सुधार" },
    icon: "fa-chart-line",
  },
  {
    id: 35,
    group: "cta",
    label: { en: "Monthly Growth", hi: "मासिक प्रगति" },
    icon: "fa-seedling",
  },
  {
    id: 36,
    group: "cta",
    label: { en: "Improvement Report", hi: "सुधार रिपोर्ट" },
    icon: "fa-clipboard-list",
  },
  {
    id: 37,
    group: "cta",
    label: { en: "Talking Growth Diary", hi: "आवाज़ डायरी" },
    icon: "fa-microphone-lines",
  },
  // ── Learn ──
  {
    id: 2,
    group: "learn",
    label: { en: "About", hi: "हमारे बारे में" },
    icon: "fa-brain",
  },
  {
    id: 11,
    group: "learn",
    label: { en: "NEP & NHM", hi: "NEP & NHM" },
    icon: "fa-landmark",
  },
  {
    id: 15,
    group: "learn",
    label: { en: "Features", hi: "विशेषताएँ" },
    icon: "fa-layer-group",
  },
  {
    id: 16,
    group: "learn",
    label: { en: "Manual", hi: "मैनुअल" },
    icon: "fa-book-open",
  },
  {
    id: 1,
    group: "learn",
    label: { en: "Disclaimer", hi: "अस्वीकृति" },
    icon: "fa-circle-info",
  },
];

const TAB_GROUPS = [
  { key: "learn", label: { en: "Learn", hi: "जानें" } },
  { key: "manage", label: { en: "Manage", hi: "प्रबंधन" } },
  { key: "assess", label: { en: "Assess", hi: "मूल्यांकन" } },
  { key: "understand", label: { en: "Understand", hi: "समझें" } },
  { key: "cta", label: { en: "CTA", hi: "शुरू करें" } },
];

function SectionDivider({ label }) {
  return (
    <div className="flex items-center gap-2 my-1">
      <div className="h-px flex-1 bg-gray-700" />
      <span className="text-xs uppercase tracking-widest text-gray-200 font-semibold shrink-0">
        {label}
      </span>
      <div className="h-px flex-1 bg-gray-700" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center text-gray-200">
      <i className="fa-solid fa-chart-simple text-lg lg:text-2xl mb-3 opacity-30" />
      <p className="text-xs text-gray-200 leading-relaxed max-w-xs">
        {message}
      </p>
    </div>
  );
}

function bandColor(range) {
  if (range === "0–15") return "border-green-700 bg-green-950 text-green-300";
  if (range === "16–30")
    return "border-yellow-700 bg-yellow-950 text-yellow-300";
  if (range === "31–50")
    return "border-orange-700 bg-orange-950 text-orange-300";
  if (range === "51–75") return "border-red-700 bg-red-950 text-red-300";
  return "border-gray-700 bg-gray-800 text-gray-100";
}

const darkChartOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: "#d1d5db", font: { size: 11 } } },
    tooltip: { titleColor: "#e2e8f0", bodyColor: "#86efac" },
    ...extra.plugins,
  },
  scales: {
    x: {
      ticks: { color: "#9ca3af", font: { size: 10 } },
      grid: { color: "#1f2937" },
    },
    y: {
      ticks: { color: "#9ca3af", font: { size: 10 } },
      grid: { color: "#1f2937" },
      beginAtZero: true,
      max: 100,
    },
  },
  ...extra,
});

// ─── Main component ───────────────────────────────────────────────────────────
export default function GrowthLoopHome() {
  const authState = useSelector((state) => state.authReducer || {});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    loading,
    assessment,
    loadAssessment,
    saveAssessment,
    updateAssessment,
    loadMyAssessments,
    saveChild,
    loadMyChildren,
  } = useGrowthLoop();

  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [lang, setLang] = useState("en");
  const [activeTab, setActiveTab] = useState(3);
  const [activeGroup, setActiveGroup] = useState("manage"); // tracks selected group
  const [child, setChild] = useState({ name: "", age: "", grade: "" });
  const [responses, setResponses] = useState({
    parent: {},
    educator: {},
    doctor: {},
    observer: {},
  });
  const [activeDeepDive, setActiveDeepDive] = useState(null);
  const [deepDiveResponses, setDeepDiveResponses] = useState({});
  const [selectedRole, setSelectedRole] = useState("parent");
  const [selectedCategory, setSelectedCategory] = useState("student");
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [accessors, setAccessors] = useState({
    parentCode: "",
    educatorCodes: "",
    doctorCodes: "",
    externalCodes: "",
  });
  const [assessmentId, setAssessmentId] = useState(null);

  const [history, setHistory] = useState([]);
  const [children, setChildren] = useState([]);
  const [saveStatus, setSaveStatus] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    (async () => {
      const childRes = await loadMyChildren();

      if (childRes?.success) {
        setChildren(childRes.data);

        if (childRes.data.length) {
          setChild(childRes.data[0]);
        }
      }

      const list = await loadMyAssessments();

      if (list?.success) {
        setHistory(list.data || []);
        
       // Auto-load the most recent assessment so Cross-View / Doctor
       // Summary / Overall Assessment reflect real saved data by default,
       // instead of staying empty until the user manually clicks "Open."
       // This also fixes assessmentId being null on fresh load, which was
       // silently causing every session's first save to create a NEW
       // assessment document instead of updating the existing one.
       if (list.data?.length) {
         const latest = [...list.data].sort(
           (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
         )[0];
         await loadAssessment(latest._id);
       }
      }
    })();
  }, []);

  const [userCode] = useState(authState?.user?.think5whyscode || "");
  const [autoGLMCode] = useState(GenerateGLMCode());
  const [autoGLPCode] = useState(GenerateGLPCode());
  const [autoGLECode] = useState(GenerateGLECode());
  const [autoGLDCode] = useState(GenerateGLDCode());
  const [autoGLXCode] = useState(GenerateGLXCode());

  const handleLogout = () => navigate("/");

  const handleTabSelect = (tab) => {
    setActiveTab(tab.id);
    setActiveGroup(tab.group);
  };

  const updateScore = (role, domainId, qIndex, value) => {
    setResponses((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [domainId]: { ...(prev[role][domainId] || {}), [qIndex]: value },
      },
    }));
  };

  const domainStats = (role, domain) => {
    const domainAnswers = responses[role]?.[domain.id] || {};
    const qCount = domain.questions[role]?.[lang]?.length || 0;
    const total = Object.values(domainAnswers).reduce(
      (a, b) => a + (Number(b) || 0),
      0,
    );
    const max = qCount * Math.max(...SCALE); // qCount * 3
    const answered = Object.keys(domainAnswers).length;
    return { total, max, answered, qCount };
  };

  const consistencyIndex = (domainId) => {
    const avgs = ROLES.map((r) => {
      const vals = Object.values(responses[r]?.[domainId] || {}).map(Number);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }).filter((v) => v !== null);
    if (avgs.length < 2) return "—";
    const spread = Math.max(...avgs) - Math.min(...avgs);
    return `${Math.round(100 - (spread / 3) * 100)}%`;
  };

  const aggregateDomainPercent = (domain) => {
    const totals = [],
      maxes = [];
    ROLES.forEach((role) => {
      const { total, max } = domainStats(role, domain);
      if (max > 0) {
        totals.push(total);
        maxes.push(max);
      }
    });
    if (!maxes.length) return 0;
    return Math.round(
      (totals.reduce((a, b) => a + b, 0) / maxes.reduce((a, b) => a + b, 0)) *
        100,
    );
  };

  const hasAnyResponse = (role) =>
    DOMAINS.some((d) => Object.keys(responses[role]?.[d.id] || {}).length > 0);

  const hasAnyResponseAll = () => ROLES.some(hasAnyResponse);

  const buildChartData = (role) => ({
    labels: DOMAINS.map((d) => d.title[lang]),
    datasets: [
      {
        label: lang === "hi" ? "स्कोर" : "Score",
        data: DOMAINS.map((d) => {
          const { total, max } = domainStats(role, d);
          return max ? Math.round((total / max) * 100) : 0;
        }),
        backgroundColor: "rgba(34,211,238,0.75)",
        borderRadius: 4,
      },
      {
        label: lang === "hi" ? "अधिकतम" : "Max",
        data: DOMAINS.map(() => 100),
        backgroundColor: "rgba(55,65,81,0.4)",
        borderRadius: 4,
      },
    ],
  });

  const doctorSummary = useMemo(() => {
    const lines = [];
    DOMAINS.forEach((d) => {
      const hits = ROLES.filter((r) => {
        const { total, max } = domainStats(r, d);
        return max && total / max >= 0.6;
      });
      if (hits.length >= 2)
        lines.push(
          `Consistent signals in ${d.title.en.toLowerCase()} across ${hits.join(" & ")}.`,
        );
    });
    return lines.length
      ? lines.join(" ") + " Non-diagnostic pattern summary only."
      : "No strong cross-setting patterns observed yet. Complete all assessments for a fuller picture.";
  }, [responses, deepDiveResponses]);

  const shouldDeepDive = (role, domain) => {
    const { total, max } = domainStats(role, domain);
    if (!max) return false;
    const severity = total / max;
    const consistency = consistencyIndex(domain.id);
    if (severity >= 0.6) return true;
    if (consistency !== "—" && parseInt(consistency) < 70) return true;
    if (role === "doctor" && severity >= 0.4) return true;
    return false;
  };

  const updateDeepDive = (domainId, qi, value) => {
    setDeepDiveResponses((prev) => {
      const current = Array.isArray(prev[domainId])
        ? [...prev[domainId]]
        : new Array(DEEP_DIVE_DOMAINS[domainId]?.[lang]?.length || 0).fill(0);
      current[qi] = value;
      return { ...prev, [domainId]: current };
    });
  };

  const deepDiveStats = (domainId) => {
    const answers = deepDiveResponses[domainId] || [];
    const total = answers.reduce((a, b) => a + (b || 0), 0);
    const max = (DEEP_DIVE_DOMAINS[domainId]?.[lang]?.length || 0) * 3;
    return { total, max };
  };

  const deepDiveInsightIndex = (domainId) => {
    const { total, max } = deepDiveStats(domainId);
    if (!max) return "—";
    if (total / max >= 0.7) return "High contextual clarity";
    if (total / max >= 0.4) return "Moderate insight emerging";
    return "Needs further observation";
  };

  const deriveDeepDiveSignals = (domainId, ddResponses) => {
    const safe = Array.isArray(ddResponses) ? ddResponses : [];
    const avg = safe.reduce((a, b) => a + (b || 0), 0) / (safe.length || 1);
    const variance =
      safe.reduce((a, b) => a + Math.abs((b || 0) - avg), 0) /
      (safe.length || 1);
    return {
      triggerDriven: variance >= 1.2,
      structureResponsive: avg <= 2.5,
      contextSpecific: variance >= 1.5,
      coRegulationHelpful: safe.some((v) => v <= 2),
    };
  };

  const allIssues = [
    ...CHILD_ISSUES_PART1,
    ...CHILD_ISSUES_PART2,
    ...CHILD_ISSUES_PART3,
  ];

  const categoryMapping = {
    student: allIssues.filter((i) =>
      [
        "screen-fragmentation",
        "executive-function",
        "emotional-dysregulation",
        "low-self-esteem",
        "foundational-learning-gaps",
        "adhd",
        "reducing-academic-pressure",
        "time-management",
        "technology-overuse",
        "peer-pressure",
        "homework-management",
        "behavioral-issues",
        "emotional-well-being",
        "health-nutrition",
        "sleep-routine",
        "communication-skills",
        "financial-literacy",
      ].includes(i.id),
    ),
    family: allIssues.filter((i) =>
      [
        "safety-security",
        "independence-responsibility",
        "cultural-identity",
        "career-guidance",
        "time-for-self-care",
        "language-skills",
        "academic-support",
        "bullying",
        "substance-abuse",
      ].includes(i.id),
    ),
    society: allIssues.filter((i) =>
      [
        "cultural-social-integration",
        "respect-for-elders",
        "responsible-social-media",
        "community-involvement",
        "time-for-family-bonding",
      ].includes(i.id),
    ),
  };

  const categoryLabels = {
    student:
      lang === "hi" ? "बालक/छात्र स्व-प्रबंधन" : "Student Self-Management",
    family: lang === "hi" ? "परिवार-केंद्रित" : "Family-Oriented",
    society: lang === "hi" ? "सामाजिक प्रभाव" : "Societal Impact",
  };

  const getIssueStats = (role, issue) => {
    if (!issue?.questions?.[role]?.[lang])
      return { total: 0, max: 0, percent: 0 };
    const answers = responses[role]?.[issue.id] || {};
    const qCount = issue.questions[role][lang].length;
    const total = Object.values(answers).reduce((s, v) => s + (v || 0), 0);
    const max = qCount * 3;
    return {
      total,
      max,
      percent: qCount > 0 ? Math.round((total / max) * 100) : 0,
    };
  };

  const issueConsistencyIndex = (issueId, role = "parent") => {
    const vals = Object.values(responses[role]?.[issueId] || {}).map(Number);
    if (vals.length < 2) return "N/A";
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance =
      vals.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / vals.length;
    return variance < 0.5 ? "High" : variance < 1.5 ? "Medium" : "Low";
  };

  const buildOverallChartData = () => ({
    labels: allIssues.map((i) => {
      const t = i.title[lang] || i.title.en;
      return t.length > 22 ? t.slice(0, 22) + "…" : t;
    }),
    datasets: [
      {
        label: lang === "hi" ? "अभिभावक" : "Parent",
        data: allIssues.map((i) => getIssueStats("parent", i).percent),
        backgroundColor: "rgba(34,211,238,0.8)",
        borderRadius: 3,
      },
      {
        label: lang === "hi" ? "शिक्षक" : "Educator",
        data: allIssues.map((i) => getIssueStats("educator", i).percent),
        backgroundColor: "rgba(251,191,36,0.8)",
        borderRadius: 3,
      },
      {
        label: lang === "hi" ? "डॉक्टर" : "Doctor",
        data: allIssues.map((i) => getIssueStats("doctor", i).percent),
        backgroundColor: "rgba(52,211,153,0.8)",
        borderRadius: 3,
      },
      {
        label: lang === "hi" ? "अवलोकक" : "Observer",
        data: allIssues.map((i) => getIssueStats("observer", i).percent),
        backgroundColor: "rgba(168,85,247,0.8)",
        borderRadius: 3,
      },
    ],
  });

  const formatDateTime = () => new Date().toISOString().replace(/[:.]/g, "-");

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const text = "GrowthLoop Child Assessment";
    const url = window.location.href;
    if (navigator.share) await navigator.share({ title: text, url });
    else
      window.open(
        `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
        "_blank",
      );
  };

  const handleSaveAssessment = async () => {
    if (!child?._id) {
      setSaveStatus({
        type: "error",
        message: "Please save the Child Profile first.",
      });

      return;
    }

    const payload = {
      childId: child._id,
      responses,
      deepDiveResponses,
      status: "Draft",
    };

    let res;

    if (assessmentId) {
      res = await updateAssessment(assessmentId, payload);
    } else {
      res = await saveAssessment(payload);

      if (res?.success) {
        setAssessmentId(res.data._id);
      }
    }

    if (res?.success) {
      const list = await loadMyAssessments();
      if (list?.success) {
        setHistory(list.data || []);
      }

      setSaveStatus({
        type: "success",
        message: "Assessment saved successfully.",
      });

      setTimeout(() => {
        setSaveStatus({ type: "", message: "" });
      }, 3000);
    } else {
      setSaveStatus({
        type: "error",
        message: res?.message || "Unable to save assessment.",
      });
      setTimeout(() => {
        setSaveStatus({ type: "", message: "" });
      }, 3000);
    }
  };

  useEffect(() => {
    if (!assessment) return;

    setAssessmentId(assessment._id);

    setChild(assessment.childId || {});

    setResponses(
      assessment.responses || {
        parent: {},
        educator: {},
        doctor: {},
        observer: {},
      },
    );

    setDeepDiveResponses(assessment.deepDiveResponses || {});
  }, [assessment]);

  const renderHeader = () => (
    <header className="sticky top-0 z-50 bg-black border-b border-gray-800">
      <div className="flex items-center justify-between px-3 py-2">
        <div>
          <div className="text-base font-bold text-yellow-200 leading-tight tracking-tight">
            🌈 GrowthLoop
          </div>
          <div className="text-xs text-gray-200 leading-tight max-w-[220px] truncate">
            {lang === "hi"
              ? "बच्चों के विकास के लिए Think5Whys द्वारा संचालित"
              : "Child development infrastructure · Think5Whys"}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex rounded overflow-hidden border border-gray-700">
            {["en", "hi"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`w-fit px-1 py-0.5 text-xs font-semibold transition rounded-lg ${
                  lang === l
                    ? "bg-cyan-500 text-black"
                    : "bg-cyan-800 text-gray-100 hover:bg-cyan-900"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {isLoggedIn ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-teal-300 max-w-[80px] truncate">
                {userName}
              </span>
              <button
                onClick={handleLogout}
                title="Logout"
                className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-xs"
              >
                ⬅️
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-7 h-7 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-xs"
            >
              🔐
            </button>
          )}
        </div>
      </div>

      <div className="px-3 py-1.5 bg-gray-950 border-t border-gray-800">
        <p className="text-xs text-gray-200 leading-relaxed">
          🚧{" "}
          <span className="text-yellow-400 font-semibold">
            Working Sample —
          </span>{" "}
          GrowthLoop is a{" "}
          <span className="text-cyan-400">
            non-clinical observational framework
          </span>
          . It <span className="text-cyan-400">does not diagnose</span> any
          condition and is{" "}
          <span className="text-cyan-400">not a substitute</span> for
          professional evaluation.
        </p>
      </div>
    </header>
  );

  const renderTabNav = () => (
    <nav className="bg-gray-950 border-b border-gray-800 sticky top-[72px] z-40">
      <div className="flex border-b border-gray-800">
        {TAB_GROUPS.map((g) => (
          <button
            key={g.key}
            onClick={() => setActiveGroup(g.key)}
            className={`w-fit px-2 py-1 text-xs font-semibold rounded-md uppercase tracking-wider transition ${
              activeGroup === g.key
                ? "text-white border-b-2 border-cyan-400 bg-blue-900"
                : "text-white border-b-2 border-cyan-400 hover:text-gray-300 bg-cyan-700"
            }`}
          >
            {g.label[lang]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-0.5">
        {tabs
          .filter((t) => t.group === activeGroup)
          .map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => handleTabSelect(t)}
                aria-pressed={isActive}
                className={`shrink-0 flex items-center gap-0.5 w-fit px-2 py-1 rounded-md text-xs font-medium transition whitespace-nowrap ${
                  isActive
                    ? "bg-blue-800 text-white"
                    : "bg-gray-800 text-gray-100 hover:bg-gray-700"
                }`}
              >
                <i className={`fa-solid ${t.icon} text-xs`} />
                {t.label[lang]}
              </button>
            );
          })}
      </div>
    </nav>
  );

  const renderCodes = () => (
    <div className="max-w-sm mx-auto space-y-3 py-2">
      <h2 className="text-sm font-bold text-yellow-300">
        {lang === "hi" ? "मूल्यांकन कोड" : "Assessment Codes"}
      </h2>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
        {[
          { label: "Master", code: autoGLMCode, color: "text-indigo-400" },
          { label: "Parent", code: autoGLPCode, color: "text-emerald-400" },
          { label: "Educator", code: autoGLECode, color: "text-rose-400" },
          { label: "Doctor", code: autoGLDCode, color: "text-amber-400" },
          { label: "External", code: autoGLXCode, color: "text-purple-400" },
        ].map(({ label, code, color }) => (
          <div key={label} className="flex justify-between items-center">
            <span className="text-xs text-gray-200">{label}</span>
            <code
              className={`font-mono text-xs ${color} bg-gray-800 px-2 py-0.5 rounded`}
            >
              {code}
            </code>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-300 leading-relaxed">
        System-generated identifiers for assessment tracking and longitudinal
        mapping.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-50 font-sans">
      {renderHeader()}
      {renderTabNav()}
      {saveStatus.message && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm font-medium transition-all ${
            saveStatus.type === "success"
              ? "border-green-600 bg-green-900/20 text-green-300"
              : "border-red-600 bg-red-900/20 text-red-300"
          }`}
        >
          {saveStatus.message}
        </div>
      )}

      <main className="flex-1 bg-gray-900 px-3 py-3">
        {/* Assess group */}
        {activeTab === 30 && <GrowthLoopDashboard />}
        {activeTab === 4 && (
          <GLAssessment
            role="parent"
            lang={lang}
            child={child}
            responses={responses}
            setResponses={setResponses}
            deepDiveResponses={deepDiveResponses}
            setDeepDiveResponses={setDeepDiveResponses}
            DOMAINS={DOMAINS}
            SCALE={SCALE}
            ROLES={ROLES}
            activeDeepDive={activeDeepDive}
            setActiveDeepDive={setActiveDeepDive}
            domainStats={domainStats}
            consistencyIndex={consistencyIndex}
            shouldDeepDive={shouldDeepDive}
            DEEP_DIVE_DOMAINS={DEEP_DIVE_DOMAINS}
            DEEP_DIVE_CTAS={DEEP_DIVE_CTAS}
            deepDiveStats={deepDiveStats}
            deepDiveInsightIndex={deepDiveInsightIndex}
            deriveDeepDiveSignals={deriveDeepDiveSignals}
            buildChartData={buildChartData}
            darkChartOptions={darkChartOptions}
            updateScore={updateScore}
            updateDeepDive={updateDeepDive}
            hasAnyResponse={hasAnyResponse}
            handlePrint={handlePrint}
            handleShare={handleShare}
            navigate={navigate}
            handleSaveAssessment={handleSaveAssessment}
          />
        )}
        {activeTab === 5 && (
          <GLAssessment
            role="educator"
            lang={lang}
            child={child}
            responses={responses}
            setResponses={setResponses}
            deepDiveResponses={deepDiveResponses}
            setDeepDiveResponses={setDeepDiveResponses}
            DOMAINS={DOMAINS}
            SCALE={SCALE}
            ROLES={ROLES}
            activeDeepDive={activeDeepDive}
            setActiveDeepDive={setActiveDeepDive}
            domainStats={domainStats}
            consistencyIndex={consistencyIndex}
            shouldDeepDive={shouldDeepDive}
            DEEP_DIVE_DOMAINS={DEEP_DIVE_DOMAINS}
            DEEP_DIVE_CTAS={DEEP_DIVE_CTAS}
            deepDiveStats={deepDiveStats}
            deepDiveInsightIndex={deepDiveInsightIndex}
            deriveDeepDiveSignals={deriveDeepDiveSignals}
            buildChartData={buildChartData}
            darkChartOptions={darkChartOptions}
            updateScore={updateScore}
            updateDeepDive={updateDeepDive}
            hasAnyResponse={hasAnyResponse}
            handlePrint={handlePrint}
            handleShare={handleShare}
            navigate={navigate}
            handleSaveAssessment={handleSaveAssessment}
          />
        )}
        {activeTab === 6 && (
          <GLAssessment
            role="doctor"
            lang={lang}
            child={child}
            responses={responses}
            setResponses={setResponses}
            deepDiveResponses={deepDiveResponses}
            setDeepDiveResponses={setDeepDiveResponses}
            DOMAINS={DOMAINS}
            SCALE={SCALE}
            ROLES={ROLES}
            activeDeepDive={activeDeepDive}
            setActiveDeepDive={setActiveDeepDive}
            domainStats={domainStats}
            consistencyIndex={consistencyIndex}
            shouldDeepDive={shouldDeepDive}
            DEEP_DIVE_DOMAINS={DEEP_DIVE_DOMAINS}
            DEEP_DIVE_CTAS={DEEP_DIVE_CTAS}
            deepDiveStats={deepDiveStats}
            deepDiveInsightIndex={deepDiveInsightIndex}
            deriveDeepDiveSignals={deriveDeepDiveSignals}
            buildChartData={buildChartData}
            darkChartOptions={darkChartOptions}
            updateScore={updateScore}
            updateDeepDive={updateDeepDive}
            hasAnyResponse={hasAnyResponse}
            handlePrint={handlePrint}
            handleShare={handleShare}
            navigate={navigate}
            handleSaveAssessment={handleSaveAssessment}
          />
        )}
        {activeTab === 31 && (
          <GLAssessment
            role="observer"
            lang={lang}
            child={child}
            responses={responses}
            setResponses={setResponses}
            deepDiveResponses={deepDiveResponses}
            setDeepDiveResponses={setDeepDiveResponses}
            DOMAINS={DOMAINS}
            SCALE={SCALE}
            ROLES={ROLES}
            activeDeepDive={activeDeepDive}
            setActiveDeepDive={setActiveDeepDive}
            domainStats={domainStats}
            consistencyIndex={consistencyIndex}
            shouldDeepDive={shouldDeepDive}
            DEEP_DIVE_DOMAINS={DEEP_DIVE_DOMAINS}
            DEEP_DIVE_CTAS={DEEP_DIVE_CTAS}
            deepDiveStats={deepDiveStats}
            deepDiveInsightIndex={deepDiveInsightIndex}
            deriveDeepDiveSignals={deriveDeepDiveSignals}
            buildChartData={buildChartData}
            darkChartOptions={darkChartOptions}
            updateScore={updateScore}
            updateDeepDive={updateDeepDive}
            hasAnyResponse={hasAnyResponse}
            handlePrint={handlePrint}
            handleShare={handleShare}
            navigate={navigate}
            handleSaveAssessment={handleSaveAssessment}
          />
        )}
        {activeTab === 29 && (
          <GLOverallAssessment
            lang={lang}
            child={child}
            responses={responses}
            deepDiveResponses={deepDiveResponses}
            domainStats={domainStats}
            deepDiveStats={deepDiveStats}
            consistencyIndex={consistencyIndex}
            deepDiveInsightIndex={deepDiveInsightIndex}
            doctorSummary={doctorSummary}
            buildChartData={buildChartData}
            darkChartOptions={darkChartOptions}
            handlePrint={handlePrint}
            navigate={navigate}
            allIssues={allIssues}
            getIssueStats={getIssueStats}
            selectedIssueId={selectedIssueId}
            setSelectedIssueId={setSelectedIssueId}
            categoryLabels={categoryLabels}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categoryMapping={categoryMapping}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            hasAnyResponseAll={hasAnyResponseAll}
            SCALE={SCALE}
            buildOverallChartData={buildOverallChartData}
            updateScore={updateScore}
            issueConsistencyIndex={issueConsistencyIndex}
            bandColor={bandColor}
          />
        )}

        {/* Understand group */}
        {activeTab === 12 && (
          <GLOpenDashboard
            lang={lang}
            DOMAINS={DOMAINS}
            POLICY_BENCHMARKS={POLICY_BENCHMARKS}
            aggregateDomainPercent={aggregateDomainPercent}
            darkChartOptions={darkChartOptions}
          />
        )}

        {activeTab === 13 && (
          <GLCrossViewDashboard
            lang={lang}
            DOMAINS={DOMAINS}
            ROLES={ROLES}
            domainStats={domainStats}
            consistencyIndex={consistencyIndex}
            hasAnyResponse={hasAnyResponse}
            responses={responses}
            darkChartOptions={darkChartOptions}
          />
        )}

        {/* Manage group */}
        {activeTab === 3 && (
          <GLChildProfile
            child={child}
            setChild={setChild}
            accessors={accessors}
            setAccessors={setAccessors}
            lang={lang}
            saveChild={saveChild}
          />
        )}
        {activeTab === 17 && renderCodes()}
        {activeTab === 18 && (
          <GLAssessors
            accessors={accessors}
            setAccessors={setAccessors}
            child={child}
            authState={authState}
            lang={lang}
          />
        )}
        {activeTab === 19 && (
          <GLHistory
            history={history}
            loading={loading}
            lang={lang}
            onOpen={(item) => {
              loadAssessment(item._id);
              setActiveGroup("assess");
              setActiveTab(29); // // Overall Assessment — shows everything loaded, regardless of which role opened
            }}
          />
        )}

        {/* CTA */}
        {activeTab === 32 && navigate("/purpledoordashboard")}
        {activeTab === 33 && navigate("/purpledoordashboard")}
        {activeTab === 34 && navigate("/purpledoordashboard")}
        {activeTab === 35 && navigate("/purpledoordashboard")}
        {activeTab === 36 && navigate("/purpledoordashboard")}
        {activeTab === 37 && navigate("/purpledoordashboard")}

        {/* Learn group */}
        {activeTab === 2 && <GrowthLoopAbout />}
        {activeTab === 11 && <GrowthLoopNEPNHM />}
        {activeTab === 15 && <GrowthLoopFnB />}
        {activeTab === 16 && <GrowthLoopUserManual />}
        {activeTab === 1 && <GrowthLoopDisclaimer />}
      </main>

      <footer className="bg-black border-t border-gray-800 py-3 px-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </p>
          <div className="flex gap-4 text-xs">
            {[
              { to: "/about-us", label: "About" },
              { to: "/privacy-policy", label: "Privacy Policy" },
              { to: "/terms-of-use", label: "Terms of Use" },
              { to: "/contact-us", label: "Contact" },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="text-gray-200 hover:text-teal-300 transition"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
